import css from './index.module.scss';
import { Button, IconNode, Modal, Typography } from '@components/base';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useParams } from 'react-router-dom';
import Images from '@assets/images';
import { DragDropUpload, SelectLabel, TextField } from '@components/common';
import { useCallback, useEffect, useState } from 'react';
import {
  createSupplierProductDocuments,
  editSupplierProductDocuments
} from '@services/supplier.service';
import notify from '@helpers/toastify-helper';
import { productDocumentSchema } from '@helpers/yup/add-product.schema';
import { IDocumentTabState, IProductDocumentData } from '../..';

interface AddProductDocumentsProps {
  open: boolean;
  onClose: () => void;
  documentTabState: IDocumentTabState;
  setDocumentTabState: React.Dispatch<React.SetStateAction<IDocumentTabState>>;
  getProductDocumentFilterOptions: () => void;
  getProductDocumentsList: (params: any) => void;
  data: IProductDocumentData | null;
}

interface IProduct {
  supplier_product_grade_id: string;
  product_grade_display_name: string;
}

interface IDocumentType {
  key: string;
  value: string;
}

interface FormType {
  document_type: IDocumentType | null;
  product: IProduct | null;
  document_description: string;
  document_file: File | null;
  remarks: string;
  document_name: string;
}

const AddProductDocuments = (props: AddProductDocumentsProps) => {
  const {
    open,
    onClose,
    documentTabState,
    setDocumentTabState,
    getProductDocumentFilterOptions,
    getProductDocumentsList,
    data
  } = props;
  const [isEdit, setIsEdit] = useState(false);
  const { productGradeOptions, documentType, currentPage } = documentTabState;

  const params = useParams();

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    getValues,
    formState: { isSubmitting }
  } = useForm<FormType>({
    resolver: yupResolver(productDocumentSchema),
    defaultValues: {
      document_type: null,
      product: null,
      document_description: '',
      document_file: null,
      remarks: '',
      document_name: ''
    }
  });

  const onFormSubmit = async (response: FormType) => {
    const reqBody = new FormData();
    reqBody.append('document_type', response.document_type?.key || '');
    reqBody.append('supplier_product_grade', response.product?.supplier_product_grade_id || '');
    reqBody.append('document_description', response.document_description);
    if (response.document_file) {
      reqBody.append('document_object', response.document_file);
    }
    reqBody.append('remarks', response.remarks);
    reqBody.append('document_name', response.document_name);
    const result = !isEdit
      ? await createSupplierProductDocuments(params.id || '', reqBody)
      : await editSupplierProductDocuments( data?.supplier_product_grade_document_id || '', reqBody);
    if (result?.success) {
      setDocumentTabState((prevState: any) => ({
        ...prevState,
        openAddProductDocumentModal: false,
        productDocumentData: null
      }));
      notify({
        message: 'Document added successfully',
        severity: 'success'
      });
      getProductDocumentsList({ page: currentPage });
    } else {
      notify({
        message: 'Something Went Wrong, Contact Tech Team',
        severity: 'error'
      });
    }
  };

  const documentWatch = useWatch({ name: 'document_file', control: control });
  const documentTypeWatch = useWatch({ name: 'document_type', control: control });
  const handleFileDrop = useCallback((file: any[]) => {
    if (file.length) {
      setValue('document_file', file[0]);
      /**
       * Prefill document_name as file name unless the document_type is OTHER
       */
      if (!(getValues('document_type')?.value == 'OTHER')) {
        setValue('document_name', file[0].name);
      }
    }
  }, []);

  const handleDeleteFile = useCallback(() => {
    setValue('document_file', null);
  }, []);

  useEffect(() => {
    if (data) {
      setIsEdit(true);
      reset({
        document_type: {
          key: data.document_type,
          value: data.doc_type_display
        },
        product: {
          product_grade_display_name: data.supplier_product_grade_display_name,
          supplier_product_grade_id: data.supplier_product_grade
        },
        document_description: data.document_description,
        document_file: null,
        remarks: data.remarks,
        document_name: data.document_name
      });
    }
  }, [data]);

  return (
    <Modal open={open} onClose={onClose} keepMounted>
      <div className={css.modalWrapper}>
        <div className={css.modalHeader}>
          <Typography variant="h4">{data ? 'Edit Document' : 'Add Document'}</Typography>
          <IconNode
            src={Images.crossBlack}
            alt="close"
            component="button"
            onClick={onClose}
            className={css.modalCloseButton}
          />
        </div>
        <form onSubmit={handleSubmit(onFormSubmit)} noValidate>
          <div className={css.formWrapper}>
            <div className={css.rowWrapper}>
              <Controller
                name="document_type"
                control={control}
                render={({ field, fieldState }) => (
                  <SelectLabel
                    {...field}
                    required
                    isSearchable
                    label="Document Type"
                    placeholder="Select Document Type"
                    getOptionLabel={(option: any) => option.value}
                    getOptionValue={(option: any) => option.key}
                    error={fieldState.invalid}
                    helperText={fieldState.error?.message}
                    options={documentType}
                  />
                )}
              />
              <Controller
                name="document_name"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    required
                    disabled={documentTypeWatch?.key !== 'OTHER'}
                    label="Document Name"
                    error={fieldState.invalid}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </div>
            <div className={css.rowWrapper}>
              <Controller
                name="product"
                control={control}
                render={({ field, fieldState }) => (
                  <SelectLabel
                    {...field}
                    required
                    isSearchable
                    label="Product"
                    placeholder="Select Product"
                    getOptionLabel={(option: any) => option.product_grade_display_name}
                    getOptionValue={(option: any) => option.supplier_product_grade_id}
                    error={fieldState.invalid}
                    helperText={fieldState.error?.message}
                    options={productGradeOptions}
                  />
                )}
              />
              <Controller
                name="document_description"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="Document Description"
                    error={fieldState.invalid}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </div>
            <Controller
              name="remarks"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label="Remarks"
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                />
              )}
            />
            <Controller
              name="document_file"
              control={control}
              render={({ field, fieldState }) => (
                <DragDropUpload
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                  required
                  onDrop={handleFileDrop}
                />
              )}
            />
            {documentWatch && (
              <div className={css.fileUploadState}>
                <Typography variant="p" className={css.fileName}>
                  {documentWatch.name}
                </Typography>
                <IconNode
                  src={Images.deleteRed}
                  alt="delete icon"
                  component="button"
                  className={css.deleteButton}
                  onClick={handleDeleteFile}
                />
              </div>
            )}
          </div>
          <div className={css.actionWrapper}>
            <Button variant="outlined-secondary" className={css.actionBtn} onClick={onClose}>
              Cancel
            </Button>
            <Button className={css.actionBtn} type="submit" disabled={isSubmitting}>
              Add Document
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default AddProductDocuments;
